import { useMemo } from "react";
import { cn } from "@/lib/utils";
import { fmtDate, todayISO } from "@/lib/format";
import { label } from "@/lib/labels";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Skeleton } from "@/components/ui/skeleton";
import { utilizationTone, UTIL_LABEL } from "@/components/planning/Gantt";
import { Gauge, TrendingUp, AlertTriangle, Boxes } from "lucide-react";

const LEFT_W = 240;

/** Heat cell colour: single brand hue by intensity, amber near capacity, rose when overbooked. */
const cellStyle = (pct) => {
  if (pct >= 100) return { className: "bg-rose-500 text-white" };
  if (pct >= 85) return { className: "bg-amber-400 text-amber-950" };
  if (pct <= 0) return { className: "bg-muted/60 text-muted-foreground" };
  const a = 0.12 + (pct / 100) * 0.78;
  return { style: { backgroundColor: `hsl(var(--primary) / ${a.toFixed(2)})` }, className: pct >= 55 ? "text-primary-foreground" : "text-foreground" };
};

export const CapacitySkeleton = () => (
  <div className="p-4 space-y-3" data-testid="planning-capacity-skeleton">
    <div className="grid grid-cols-4 gap-3">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>
    {Array.from({ length: 8 }).map((_, i) => <div key={i} className="flex gap-2"><Skeleton className="h-9 w-56" /><Skeleton className="h-9 flex-1" /></div>)}
  </div>
);

/**
 * Capacity heatmap: module type x week bucket. Data from GET /planning/capacity.
 */
export const CapacityView = ({ data, onCellClick, className }) => {
  const today = todayISO();
  const stats = useMemo(() => {
    if (!data) return null;
    const totals = data.totals || [];
    const avg = totals.length ? Math.round(totals.reduce((s, t) => s + t.pct, 0) / totals.length) : 0;
    const peak = totals.reduce((m, t, i) => (t.pct > (m?.pct ?? -1) ? { ...t, bucket: data.buckets[i] } : m), null);
    const nowIdx = data.buckets.findIndex((b) => b.start <= today && today < b.end);
    const now = nowIdx >= 0 ? totals[nowIdx] : null;
    const critical = data.rows.filter((r) => r.cells.some((c) => c.pct >= 85)).length;
    const totalModules = data.rows.reduce((s, r) => s + r.total, 0);
    return { avg, peak, now, critical, totalModules };
  }, [data, today]);

  if (!data || !stats) return <CapacitySkeleton />;

  const months = [];
  for (const b of data.buckets) {
    const m = fmtDate(b.start, "MMM yyyy");
    if (months.length && months[months.length - 1].label === m) months[months.length - 1].span += 1;
    else months.push({ label: m, span: 1 });
  }

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn("flex h-full flex-col", className)} data-testid="planning-capacity-view">
        <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-4 border-b">
          {[
            { icon: Gauge, label: "Utnyttelse nå", value: stats.now ? `${stats.now.pct} %` : "–", hint: stats.now ? `${stats.now.busy} av ${stats.now.busy + stats.now.available} moduler i bruk` : "Ingen data for denne uken", testId: "capacity-kpi-now" },
            { icon: TrendingUp, label: "Snitt i perioden", value: `${stats.avg} %`, hint: `${data.buckets.length} uker`, testId: "capacity-kpi-avg" },
            { icon: TrendingUp, label: "Toppuke", value: stats.peak ? `${stats.peak.pct} %` : "–", hint: stats.peak ? `${stats.peak.bucket.label} · ${fmtDate(stats.peak.bucket.start, "d. MMM")}` : "", testId: "capacity-kpi-peak" },
            { icon: AlertTriangle, label: "Typer nær kapasitet", value: stats.critical, hint: "≥ 85 % i minst én uke", tone: stats.critical ? "warn" : "ok", testId: "capacity-kpi-critical" },
          ].map((k) => (
            <div key={k.label} className="rounded-md border px-4 py-3" data-testid={k.testId}>
              <div className="text-[11.5px] text-muted-foreground">{k.label}</div>
              <div className={cn("mt-1 font-mono text-xl tabular tracking-tight", k.tone === "warn" && "text-amber-700", k.tone === "ok" && "text-emerald-700")}>{k.value}</div>
              <div className="text-[11px] text-muted-foreground truncate">{k.hint}</div>
            </div>
          ))}
        </div>

        <div className="flex-1 min-h-0 overflow-auto scrollbar-thin">
          <table className="border-separate border-spacing-0 text-xs" style={{ minWidth: LEFT_W + data.buckets.length * 46 }} data-testid="planning-capacity-table">
            <thead className="sticky top-0 z-20 bg-card">
              <tr>
                <th className="sticky left-0 z-30 bg-card border-b border-r px-3 py-1.5 text-left font-medium text-muted-foreground" style={{ width: LEFT_W, minWidth: LEFT_W }} rowSpan={2}>Modultype</th>
                {months.map((m, i) => <th key={i} colSpan={m.span} className="border-b border-l px-2 py-1 text-left font-semibold text-[11px] whitespace-nowrap">{m.label}</th>)}
              </tr>
              <tr>
                {data.buckets.map((b) => (
                  <th key={b.key} className={cn("border-b border-l px-1 py-1 text-center font-medium tabular text-muted-foreground", b.start <= today && today < b.end && "text-primary font-semibold")} style={{ minWidth: 46 }}>{b.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="font-semibold" data-testid="capacity-totals-row">
                <td className="sticky left-0 z-10 bg-card border-b border-r px-3 py-2">
                  <div className="flex items-center gap-2"><Boxes className="h-3.5 w-3.5 text-muted-foreground" /> Alle typer <span className="ml-auto font-mono text-muted-foreground tabular">{stats.totalModules}</span></div>
                </td>
                {data.totals.map((c) => {
                  const st = cellStyle(c.pct);
                  return (
                    <td key={c.key} className="border-b border-l p-0.5">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className={cn("h-8 rounded-md flex items-center justify-center tabular", st.className)} style={st.style}>{c.pct}%</div>
                        </TooltipTrigger>
                        <TooltipContent className="text-xs"><div className="font-semibold">Alle typer · uke {fmtDate(c.key, "w")}</div><div className="tabular">{c.busy} i bruk · {c.available} ledige</div></TooltipContent>
                      </Tooltip>
                    </td>
                  );
                })}
              </tr>
              {data.rows.map((r) => (
                <tr key={r.module_type_id} className="group" data-testid={`capacity-row-${r.code}`}>
                  <td className="sticky left-0 z-10 bg-card border-b border-r px-3 py-1.5 group-hover:bg-muted/40">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[11px] rounded bg-muted px-1.5 py-0.5">{r.code}</span>
                      <span className="truncate">{r.name}</span>
                      <span className="ml-auto shrink-0 font-mono text-muted-foreground tabular">{r.total}</span>
                    </div>
                    <div className="text-[10px] text-muted-foreground pl-0.5">{label(r.category)} · topp {r.peak}/{r.total}</div>
                  </td>
                  {r.cells.map((c, i) => {
                    const st = cellStyle(c.pct);
                    const b = data.buckets[i];
                    return (
                      <td key={c.key} className="border-b border-l p-0.5">
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button type="button" onClick={() => onCellClick?.(r, b, c)} className={cn("h-9 w-full rounded-md flex flex-col items-center justify-center leading-none tabular transition-[transform,box-shadow] duration-150 hover:scale-[1.04] hover:shadow-pop focus-visible:ring-2 focus-visible:ring-ring", st.className)} style={st.style} data-testid={`capacity-cell-${r.code}-${c.key}`}>
                              <span className="text-[11px] font-semibold">{c.busy}</span>
                              <span className="text-[9px] opacity-80">/{r.total}</span>
                            </button>
                          </TooltipTrigger>
                          <TooltipContent className="text-xs">
                            <div className="font-semibold">{r.code} · {b.label} ({fmtDate(b.start, "d. MMM")})</div>
                            <div className="tabular">{c.busy} i bruk · {c.available} ledige · {c.pct} %</div>
                            <div className="text-muted-foreground">{UTIL_LABEL[utilizationTone(c.pct)]} · klikk for å se modulene</div>
                          </TooltipContent>
                        </Tooltip>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="border-t px-4 py-2 flex flex-wrap items-center gap-4 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-muted/80" /> 0 %</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-primary/30" /> Lav</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-primary/70" /> Moderat</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-amber-400" /> ≥ 85 %</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded-sm bg-rose-500" /> Overbooket</span>
          <span className="ml-auto">Tall = moduler i bruk / totalt i uken</span>
        </div>
      </div>
    </TooltipProvider>
  );
};
